import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { useAuth } from '@/context/AuthContext';

const ProjectContext = createContext(null);

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedProjectId, setSelectedProjectIdState] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem('selectedProjectId') || null;
  });
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

  // Load projects list
  const loadProjects = useCallback(async () => {
    // Don't load if not authenticated
    if (!user) {
      setLoadingProjects(false);
      return;
    }
    
    try {
      setLoadingProjects(true);
      const response = await api.projects.getAll();
      setProjects(response.data || []);
      
      // If we have a selected project ID, validate it exists
      const storedId = localStorage.getItem('selectedProjectId');
      if (storedId) {
        const projectExists = (response.data || []).find(p => p.id === storedId);
        if (!projectExists && response.data?.length > 0) {
          // If stored project doesn't exist, clear selection
          localStorage.removeItem('selectedProjectId');
          setSelectedProjectIdState(null);
          setSelectedProject(null);
        } else if (projectExists) {
          setSelectedProject(projectExists);
        }
      }
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  }, [user]);

  // Reload projects when user changes (login/logout)
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Set selected project ID and persist to localStorage
  const setSelectedProjectId = useCallback((projectId) => {
    if (projectId) {
      localStorage.setItem('selectedProjectId', projectId);
      setSelectedProjectIdState(projectId);
      // Find and set the full project object
      const project = projects.find(p => p.id === projectId);
      if (project) {
        setSelectedProject(project);
      }
    } else {
      localStorage.removeItem('selectedProjectId');
      setSelectedProjectIdState(null);
      setSelectedProject(null);
    }
  }, [projects]);

  // Update selected project when projects list changes
  useEffect(() => {
    if (selectedProjectId && projects.length > 0) {
      const project = projects.find(p => p.id === selectedProjectId);
      if (project) {
        setSelectedProject(project);
      }
    }
  }, [selectedProjectId, projects]);

  return (
    <ProjectContext.Provider value={{
      selectedProjectId,
      selectedProject,
      setSelectedProjectId,
      projects,
      loadingProjects,
      refreshProjects: loadProjects
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};
